import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import RGL, { WidthProvider, Layout } from "react-grid-layout";
import TotalOfferperStatus from "./responsive/Wedgits/total-offer-status";
import { StaticDashboard } from "./responsive/Wedgits/StaticDashboard";
import { TbFileInvoice } from "react-icons/tb";
import { LuFileBox } from "react-icons/lu";
import { GoPackageDependencies } from "react-icons/go";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { FaUsers } from "react-icons/fa";
import OrdersBList from "./responsive/Wedgits/OrdersBList";
import { isUndefined } from "lodash";
import { ROLE } from "@/types";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import TrenderQuatationsList from "./responsive/Wedgits/treder-quatations-list";
import CompleteProfile from "./responsive/Wedgits/CompleteProfile";
import useProfile from "@/hooks/use-profile";
import { useDispatch } from "react-redux";
import { getMyCompany } from "@/store/authSlice";
import { Box, Paper } from "@mui/material";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const ReactGridLayout = WidthProvider(RGL);

// Layout configurations for different screen sizes
const getLayout = (screenSize: string): Layout[] => {
  switch (screenSize) {
    case "extraLarge":
      return [
        { i: "1", x: 0, y: 0, w: 3, h: 5, static: true },
        { i: "2", x: 3, y: 0, w: 3, h: 5, static: true },
        { i: "3", x: 6, y: 0, w: 3, h: 5, static: true },
        { i: "4", x: 9, y: 0, w: 3, h: 5, static: true },
        { i: "5", x: 0, y: 5, w: 3, h: 20, static: true },
        { i: "6", x: 3, y: 5, w: 9, h: 17, static: true },
        { i: "7", x: 3, y: 22, w: 9, h: 17, static: true },
      ];
    case "large":
    case "desktop":
    case "tablet":
      return [
        { i: "1", x: 0, y: 0, w: 6, h: 5, static: true },
        { i: "2", x: 6, y: 0, w: 6, h: 5, static: true },
        { i: "3", x: 0, y: 5, w: 6, h: 5, static: true },
        { i: "4", x: 6, y: 5, w: 6, h: 5, static: true },
        { i: "5", x: 0, y: 10, w: 12, h: 18, static: true },
        { i: "6", x: 0, y: 28, w: 12, h: 15, static: true },
        { i: "7", x: 0, y: 43, w: 12, h: 15, static: true },
      ];
    case "mobile":
      return [
        { i: "1", x: 0, y: 0, w: 12, h: 4, static: true },
        { i: "2", x: 0, y: 4, w: 12, h: 4, static: true },
        { i: "3", x: 0, y: 8, w: 12, h: 4, static: true },
        { i: "4", x: 0, y: 12, w: 12, h: 4, static: true },
        { i: "5", x: 0, y: 16, w: 12, h: 15, static: true },
        { i: "6", x: 0, y: 31, w: 12, h: 25, static: true },
        { i: "7", x: 0, y: 56, w: 12, h: 25, static: true },
      ];
    default:
      return [
        { i: "1", x: 0, y: 0, w: 6, h: 5, static: true },
        { i: "2", x: 6, y: 0, w: 6, h: 5, static: true },
        { i: "3", x: 0, y: 5, w: 6, h: 5, static: true },
        { i: "4", x: 6, y: 5, w: 6, h: 5, static: true },
        { i: "5", x: 0, y: 10, w: 12, h: 18, static: true },
        { i: "6", x: 0, y: 28, w: 12, h: 15, static: true },
        { i: "7", x: 0, y: 43, w: 12, h: 15, static: true },
      ];
  }
};

// Get row height based on screen size
const getRowHeight = (screenSize: string): number => {
  switch (screenSize) {
    case "extraLarge":
      return 18;
    case "mobile":
    case "tablet":
    case "desktop":
    case "large":
    default:
      return 20;
  }
};

// Get container padding based on screen size
const getContainerPadding = (screenSize: string): string => {
  switch (screenSize) {
    case "extraLarge":
      return "24px 32px";
    case "large":
    case "desktop":
      return "20px 24px";
    case "tablet":
      return "16px 20px";
    case "mobile":
      return "12px 16px";
    default:
      return "16px";
  }
};

const Responsive: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const dispatch = useDispatch();

  // Media queries for different screen sizes
  const isExtraLarge = useMediaQuery({ minWidth: 1500 });
  const isLarge = useMediaQuery({ minWidth: 1200, maxWidth: 1499 });
  const isDesktop = useMediaQuery({ minWidth: 992, maxWidth: 1199 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 });
  const isMobile = useMediaQuery({ maxWidth: 767 });

  // Determine current screen size
  const getScreenSize = (): string => {
    if (isExtraLarge) return "extraLarge";
    if (isLarge) return "large";
    if (isDesktop) return "desktop";
    if (isTablet) return "tablet";
    if (isMobile) return "mobile";
    return "desktop"; // default fallback
  };

  const screenSize = getScreenSize();
  const layout = getLayout(screenSize);
  const rowHeight = getRowHeight(screenSize);
  const containerPadding = getContainerPadding(screenSize);

  // State management
  const [completProfile, setCompletProfile] = useState({
    profile: true,
    company: true,
  });
  const [staticDashboard, setStaticDashboard] = useState<any>(null);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;
  const { getUserProfile } = useProfile();

  useEffect(() => {
    getUserProfile()
      .then((res) => {
        dispatch(getMyCompany(res.data.company));
        setStaticDashboard(res.data.buyerStatistics);
        setCompletProfile({
          company: Boolean(res.data.company),
          profile: Boolean(res.data.user.phoneNumber),
        });
      })
      .catch((_) => {
        setCompletProfile({
          profile: true,
          company: true,
        });
      });
  }, []);

  const level = () => {
    if (!completProfile.company && !completProfile.profile) {
      return "0%";
    } else if (completProfile.company && completProfile.profile) {
      return false;
    } else {
      return "50%";
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: containerPadding,
        direction: "ltr",
        overflowX: "hidden",
      }}
    >
      {level() && (
        <Box sx={{ mb: 3 }}>
          <CompleteProfile
            isCompany={!!completProfile?.company}
            isProfile={!!completProfile?.profile}
            level={level()}
          />
        </Box>
      )}

      <Box
        sx={{
          "& .react-grid-layout": {
            background: "transparent",
          },
          "& .react-grid-item": {
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              transform: "translateY(-2px)",
            },
          },
        }}
      >
        <ReactGridLayout
          className="layout"
          layout={layout}
          rowHeight={rowHeight}
          isDraggable={false}
          isResizable={false}
          cols={12}
          margin={[16, 16]}
          containerPadding={[0, 0]}
        >
          {/* Stats Cards */}
          <Paper
            key="1"
            sx={{
              boxShadow: "none",
              borderRadius: 2,
              background: "white",
              overflow: "hidden",
            }}
          >
            <Box sx={{ height: "100%" }}>
              <StaticDashboard
                day={t("general.comparedTo30Days")}
                percentage={"٤%"}
                logo={<FaUsers />}
                title={t("general.suppliers")}
                subTitle={staticDashboard?.totalSuppliers}
              />
            </Box>
          </Paper>

          <Paper
            key="2"
            sx={{
              borderRadius: 2,
              boxShadow: "none",
              background: "white",
              overflow: "hidden",
            }}
          >
            <Box sx={{ height: "100%" }}>
              <StaticDashboard
                day={t("general.comparedTo30Days")}
                percentage={"٤%"}
                logo={<LuFileBox />}
                title={t("general.openOpportunities")}
                subTitle={staticDashboard?.totalOpenTenders}
              />
            </Box>
          </Paper>

          <Paper
            key="3"
            sx={{
              borderRadius: 2,
              boxShadow: "none",
              background: "white",
              overflow: "hidden",
            }}
          >
            <Box sx={{ height: "100%" }}>
              <StaticDashboard
                day={t("general.comparedTo30Days")}
                percentage={"٤%"}
                logo={<GoPackageDependencies />}
                title={
                  userRole === ROLE.SELLER
                    ? t("general.sales")
                    : t("general.Purchases")
                }
                subTitle={staticDashboard?.totalCompletedOrders}
              />
            </Box>
          </Paper>

          <Paper
            key="4"
            sx={{
              borderRadius: 2,
              boxShadow: "none",
              background: "white",
              overflow: "hidden",
            }}
          >
            <Box sx={{ height: "100%" }}>
              <StaticDashboard
                day={t("general.comparedTo30Days")}
                percentage={"٤%"}
                logo={<TbFileInvoice />}
                title={t("statistics.Offers")}
                subTitle={staticDashboard?.totalTenderQuotations}
              />
            </Box>
          </Paper>

          {/* Charts and Lists */}
          <Paper
            key="5"
            elevation={1}
            sx={{
              boxShadow: "none",
              borderRadius: 2,
              background: "white",
            }}
          >
            <TotalOfferperStatus />
          </Paper>

          <Paper
            key="6"
            elevation={1}
            sx={{
              boxShadow: "none",
              borderRadius: 2,
              background: "white",
            }}
          >
            <TrenderQuatationsList />
          </Paper>

          <Paper
            key="7"
            elevation={1}
            sx={{
              borderRadius: 2,
              boxShadow: "none",
              background: "white",
            }}
          >
            <OrdersBList />
          </Paper>
        </ReactGridLayout>
      </Box>
    </Box>
  );
};

export default Responsive;
